import React, { useState, useEffect } from 'react'
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  MapPin,
  Calendar,
  RefreshCw,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { format, subDays, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns'
import { sessionAPI, huntAPI, analyticsAPI } from '../services/api'

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null)
  const [sessions, setSessions] = useState([])
  const [hunts, setHunts] = useState([])
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30') // days
  const [selectedHunt, setSelectedHunt] = useState('all')
  const [viewMode, setViewMode] = useState('daily') // daily or monthly

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const [sessionsData, huntsData, overviewData, huntAnalyticsData, revenueData] = await Promise.all([
        sessionAPI.getAll(),
        huntAPI.getAll(),
        analyticsAPI.getOverview(parseInt(timeRange)),
        analyticsAPI.getHuntAnalytics(),
        analyticsAPI.getRevenueAnalytics()
      ])

      setSessions(sessionsData.data || [])
      setHunts(huntsData.data || [])
      
      // Use backend analytics data
      const analyticsData = {
        overview: overviewData.success ? overviewData.data : null,
        huntAnalytics: huntAnalyticsData.success ? huntAnalyticsData.data : [],
        revenue: revenueData.success ? revenueData.data : null,
        viewMode
      }
      setAnalytics(analyticsData)
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const calculateAnalytics = (sessions, hunts, days) => {
    const endDate = new Date()
    const startDate = subDays(endDate, parseInt(days))
    
    // Filter sessions by date range
    const filteredSessions = sessions.filter(session => {
      const sessionDate = new Date(session.created_at)
      return sessionDate >= startDate && sessionDate <= endDate
    })

    // Filter by selected hunt
    const huntFilteredSessions = selectedHunt === 'all' 
      ? filteredSessions 
      : filteredSessions.filter(s => s.hunt_name === selectedHunt)

    // Revenue calculations
    const totalRevenue = huntFilteredSessions.length * 25 // Assuming $25 per session
    const completedSessions = huntFilteredSessions.filter(s => s.status === 'completed')
    const activeSessions = huntFilteredSessions.filter(s => s.status === 'active')
    const expiredSessions = huntFilteredSessions.filter(s => s.status === 'expired')

    // Completion rate
    const completionRate = huntFilteredSessions.length > 0 
      ? (completedSessions.length / huntFilteredSessions.length) * 100 
      : 0

    // Average completion time (for completed sessions)
    const avgCompletionTime = completedSessions.length > 0
      ? completedSessions.reduce((total, session) => {
          if (session.started_at && session.completed_at) {
            const startTime = new Date(session.started_at)
            const endTime = new Date(session.completed_at)
            return total + (endTime - startTime) / (1000 * 60 * 60) // hours
          }
          return total
        }, 0) / completedSessions.length
      : 0

    // Hunt popularity
    const huntStats = hunts.map(hunt => {
      const huntSessions = filteredSessions.filter(s => s.hunt_name === hunt.name)
      return {
        name: hunt.name,
        sessions: huntSessions.length,
        revenue: huntSessions.length * 25,
        completionRate: huntSessions.length > 0 
          ? (huntSessions.filter(s => s.status === 'completed').length / huntSessions.length) * 100 
          : 0
      }
    }).sort((a, b) => b.sessions - a.sessions)

    // Daily trends
    const dailyData = eachDayOfInterval({ start: startDate, end: endDate }).map(date => {
      const daySessions = filteredSessions.filter(session => {
        const sessionDate = new Date(session.created_at)
        return sessionDate >= startOfDay(date) && sessionDate <= endOfDay(date)
      })
      
      return {
        date: format(date, 'MMM dd'),
        sessions: daySessions.length,
        revenue: daySessions.length * 25,
        completed: daySessions.filter(s => s.status === 'completed').length
      }
    })

    // Customer insights
    const uniqueCustomers = new Set(huntFilteredSessions.map(s => s.customer_email)).size
    const repeatCustomers = huntFilteredSessions.reduce((acc, session) => {
      const email = session.customer_email
      acc[email] = (acc[email] || 0) + 1
      return acc
    }, {})
    const repeatCustomerCount = Object.values(repeatCustomers).filter(count => count > 1).length

    return {
      overview: {
        totalSessions: huntFilteredSessions.length,
        totalRevenue,
        completionRate: Math.round(completionRate * 100) / 100,
        avgCompletionTime: Math.round(avgCompletionTime * 100) / 100,
        uniqueCustomers,
        repeatCustomerCount
      },
      statusBreakdown: {
        completed: completedSessions.length,
        active: activeSessions.length,
        expired: expiredSessions.length
      },
      huntStats,
      dailyData,
      timeRange: days
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [timeRange, selectedHunt, viewMode])

  const handleRefresh = () => {
    fetchAnalytics()
  }

  const handleExport = () => {
    // Export analytics data as CSV
    const csvData = analytics?.dailyData.map(day => 
      `${day.date},${day.sessions},${day.revenue},${day.completed}`
    ).join('\n')
    
    const csvContent = `Date,Sessions,Revenue,Completed\n${csvData}`
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-${timeRange}days.csv`
    a.click()
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-6 h-6 lg:w-8 lg:h-8 text-primary-600" />
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm lg:text-base text-gray-600">Detailed insights and performance metrics</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          
          <select
            value={selectedHunt}
            onChange={(e) => setSelectedHunt(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
          >
            <option value="all">All Hunts</option>
            {hunts.map(hunt => (
              <option key={hunt.id} value={hunt.name}>{hunt.name}</option>
            ))}
          </select>
          
          <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
            <button
              onClick={() => setViewMode('daily')}
              className={`px-3 py-2 rounded text-sm font-medium ${
                viewMode === 'daily' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 py-2 rounded text-sm font-medium ${
                viewMode === 'monthly' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
          </div>
          
          <button
            onClick={handleRefresh}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={handleExport}
            className="btn-primary flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
            <div className="ml-4 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Total Revenue (GBP)</p>
              <p className="text-2xl font-bold text-gray-900 truncate">
                £{analytics?.revenue?.total?.gross?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500">After commission: £{analytics?.revenue?.total?.net?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4 min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900 truncate">
                {analytics?.overview?.totalSessions || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900 truncate">
                {Math.round(analytics?.overview?.completionRate || 0)}%
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4 min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-600">Avg Completion Time</p>
              <p className="text-2xl font-bold text-gray-900 truncate">
                {(() => {
                  const hours = Math.round(analytics?.overview?.avgCompletionHours || 0)
                  if (hours === 0) return '0h'
                  if (hours < 1) return '<1h'
                  return `${hours}h`
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trends Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {viewMode === 'monthly' ? 'Monthly Trends' : 'Daily Trends'}
          </h3>
          <div className="space-y-3">
            {viewMode === 'monthly' ? (
              // Monthly view
              (analytics?.trends || []).map((month, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{month.monthName}</h4>
                    <div className="text-sm text-gray-600">
                      <span className="text-green-600 font-medium">${month.totalRevenue}</span>
                      <span className="mx-2">•</span>
                      <span>{month.totalSessions} sessions</span>
                    </div>
                  </div>
                  
                  {/* Daily breakdown */}
                  <div className="space-y-2">
                    {(month.dailyBreakdown || []).slice(0, 7).map((day, dayIndex) => (
                      <div key={dayIndex} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-2">
                        <span className="text-gray-600">{format(new Date(day.date), 'MMM dd')}</span>
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-600">{day.sessions} sessions</span>
                          <span className="text-green-600 font-medium">${day.revenue}</span>
                          <span className="text-blue-600">{day.completed} completed</span>
                        </div>
                      </div>
                    ))}
                    {month.dailyBreakdown.length > 7 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{month.dailyBreakdown.length - 7} more days
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              // Daily view
              (analytics?.revenue?.daily || []).map((day, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{format(new Date(day.date), 'MMM dd')}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-600">{day.sessions} sessions</span>
                    <span className="text-green-600 font-medium">£{day.gross.toFixed(2)}</span>
                    <span className="text-red-600">-£{day.commission.toFixed(2)}</span>
                    <span className="text-blue-600 font-medium">£{day.net.toFixed(2)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Individual Hunt Revenue */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Individual Hunt Revenue & Performance</h3>
          <div className="space-y-4">
            {analytics?.huntAnalytics?.map((hunt, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold text-gray-900">{hunt.name}</span>
                    <span className="text-sm text-gray-500">
                      ({hunt.basePrice} {hunt.currency})
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Completion Rate</div>
                    <div className="text-lg font-bold text-blue-600">{hunt.completionRate}%</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Total Sessions</div>
                    <div className="text-lg font-bold text-gray-900">{hunt.totalSessions}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Completed</div>
                    <div className="text-lg font-bold text-green-600">{hunt.completedSessions}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Active</div>
                    <div className="text-lg font-bold text-orange-600">{hunt.activeSessions}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-600">Recent (30d)</div>
                    <div className="text-lg font-bold text-purple-600">{hunt.recentSessions}</div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Gross Revenue</div>
                      <div className="text-lg font-bold text-green-600">
                        {hunt.currency === 'GBP' ? '£' : hunt.currency === 'EUR' ? '€' : '$'}
                        {hunt.revenue.gross.original.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        (£{hunt.revenue.gross.gbp.toFixed(2)})
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Commission ({(hunt.revenue?.commission?.percentage || 0).toFixed(1)}%)</div>
                      <div className="text-lg font-bold text-red-600">
                        £{hunt.revenue.commission.gbp.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600">Net Revenue</div>
                      <div className="text-lg font-bold text-blue-600">
                        £{hunt.revenue.net.gbp.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Summary (All Hunts)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">Total Revenue</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Gross Revenue (GBP)</span>
                <span className="text-lg font-bold text-green-600">
                  £{analytics?.revenue?.total?.gross?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Overall Commission ({analytics?.revenue?.total?.commissionRate || 25}%)</span>
                        <span className="text-lg font-bold text-red-600">
                          £{analytics?.revenue?.total?.commission?.toFixed(2) || '0.00'}
                        </span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                <span className="text-sm font-medium text-gray-700">Net Revenue (GBP)</span>
                <span className="text-xl font-bold text-blue-600">
                  £{analytics?.revenue?.total?.net?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">Recent Revenue (Last 30 Days)</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Gross Revenue (GBP)</span>
                <span className="text-lg font-bold text-green-600">
                  £{analytics?.revenue?.recent?.gross?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Overall Commission ({analytics?.revenue?.recent?.commissionRate || 25}%)</span>
                        <span className="text-lg font-bold text-red-600">
                          £{analytics?.revenue?.recent?.commission?.toFixed(2) || '0.00'}
                        </span>
              </div>
              <div className="flex justify-between items-center border-t border-gray-200 pt-2">
                <span className="text-sm font-medium text-gray-700">Net Revenue (GBP)</span>
                <span className="text-xl font-bold text-blue-600">
                  £{analytics?.revenue?.recent?.net?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Insights</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Unique Customers</span>
              <span className="text-lg font-semibold text-gray-900">
                {analytics?.overview?.uniqueCustomers || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Repeat Customers</span>
              <span className="text-lg font-semibold text-gray-900">
                {analytics?.overview?.repeatCustomerCount || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Customer Retention</span>
              <span className="text-lg font-semibold text-gray-900">
                {analytics?.overview?.uniqueCustomers > 0 
                  ? Math.round((analytics.overview.repeatCustomerCount / analytics.overview.uniqueCustomers) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Completed</span>
              <span className="text-lg font-semibold text-green-600">
                {analytics?.overview?.completedSessions || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active</span>
              <span className="text-lg font-semibold text-blue-600">
                {analytics?.overview?.activeSessions || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Expired</span>
              <span className="text-lg font-semibold text-red-600">
                {analytics?.overview?.expiredSessions || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Revenue per Session</span>
              <span className="text-lg font-semibold text-gray-900">
                ${analytics?.overview?.totalSessions > 0 
                  ? Math.round(analytics.overview.totalRevenue / analytics.overview.totalSessions)
                  : 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Sessions per Day</span>
              <span className="text-lg font-semibold text-gray-900">
                {analytics?.overview?.totalSessions > 0 
                  ? Math.round(analytics.overview.totalSessions / parseInt(timeRange))
                  : 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Revenue per Day</span>
              <span className="text-lg font-semibold text-gray-900">
                ${analytics?.overview?.totalRevenue > 0 
                  ? Math.round(analytics.overview.totalRevenue / parseInt(timeRange))
                  : 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics
